import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Group, IconList, Overlay, Typography, useModalPortal, Wrapper } from '@screentone/core';
import { cloneDeep, isEqual } from 'lodash';

import { useAllessehContentUpdate } from 'contexts/allesseh-content-update/useAllessehContentUpdate';
import { useAltSummOverrideContext } from 'contexts/altsumm-override/AltSummOverrideContext';
import { useContextMenuActions } from 'contexts/context-menu-actions/useContextMenuActions';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { useItp } from 'contexts/itp/useItp';
import { useSummarianContext } from 'contexts/summarian/useSummarianContext';
import { ItpDto, ItpIssueStatus } from 'data/generated/graphql';
import { useMediaForm } from 'hooks/altsumm/useMediaForm';
import { useMediaPreview } from 'hooks/altsumm/useMediaPreview';
import { usePublicationSettings } from 'hooks/publication-settings';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import { SnippetyChartData, SnippetyResponseWithImage, VideoDataForSummarian } from 'hooks/useSnippetyQuery';
import { arrayEquals } from 'utils/array';
import type { AltSummField } from 'utils/constants';
import { sleep } from 'utils/temp';
import { decodeHtmlEntities, encodeHtmlEntities } from 'utils/text';
import { formatBulletLink } from 'utils/url';
import styles from './AltSummModal.module.scss';
import {
  buildIframeFromAddIframeData,
  ControlledAltSumm,
  DEFAULT_CONTROLLED_ALT_SUMM,
  DEFAULT_MEDIA_ALT_SUMM
} from './AltSummModal.utils';
import { AddIframeData, AddIframeModal } from './components/add-iframe-modal/AddIframeModal';
import { BulletInput } from './components/bullet-input/BulletInput';
import { MediaInputButton } from './components/media-input/components/MediaInputButton';
import { MediaInputForm } from './components/media-input/components/MediaInputForm';
import { MediaInputPreview } from './components/media-input/components/MediaInputPreview';
import { MediaTypes } from './components/media-input/MediaInput.utils';

import { getDefaultImage, useAltSummFields } from 'contexts/summarian/summarian.utils';

interface AltSumModalProps {
  setIsModalOpenControlled?: (isOpen: boolean) => void;
  isModalOpenControlled?: boolean;
  content: AllessehContent;
  viewOnly?: boolean;
}

export const AltSummModal = ({
  content,
  setIsModalOpenControlled,
  isModalOpenControlled,
  viewOnly = false
}: AltSumModalProps) => {
  const { isModalOpen, setIsModalOpen } = useContextMenuActions();
  const { renderNode } = useModalPortal();
  const { data: publicationSettingsResp } = usePublicationSettings();
  const disabledAltSummFields = new Set<AltSummField>(
    (publicationSettingsResp?.publicationSetting.altSummDisabledFields ?? []) as AltSummField[]
  );

  const [controlledAltSum, setControlledAltSum] = useState<ControlledAltSumm>(DEFAULT_CONTROLLED_ALT_SUMM);
  const [isUpdating, setIsUpdating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [inputType, setInputType] = useState<MediaTypes | ''>('');

  const flashlineRef = useRef<HTMLTextAreaElement>(null);
  const headlineRef = useRef<HTMLTextAreaElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const publicationSettings = publicationSettingsResp?.publicationSetting;
  const imagePreviewRef = useRef<HTMLImageElement>(null);

  const { root } = useDataModelContext();
  const { setHasAltSummChanged, updateIssue } = useItp();
  const { selectedAltSummVariant: altSummVariant } = useSummarianContext();
  const { setOverride } = useAltSummOverrideContext();

  const [displayNewBulletInput, setDisplayNewBulletInput] = useState(false);
  const [isIframeModalOpen, setIsIframeModalOpen] = useState(false);
  const hasUserChangedMediaRef = useRef(false);
  // Prevents external Allesseh updates from overwriting user edits after the modal is opened.
  // Set to true after the initial seed; reset on unmount (modal close).
  const initializedRef = useRef(false);

  const originId = content.data.attributes.base_doc_id || content.data.id;
  const updatedContent = useAllessehContentUpdate(originId, { initialContent: content });
  const resolvedContent = updatedContent ?? content;
  const {
    flashline,
    headline,
    body,
    image,
    imageCredit,
    video,
    bullets,
    chartlosId,
    chart,
    subhead,
    videoImage,
    iframe
  } = useAltSummFields(resolvedContent, altSummVariant);

  const itpRoot = root as ItpDto['root'] | null;

  const handleChangeForImage = useCallback((imageData: SnippetyResponseWithImage | null) => {
    hasUserChangedMediaRef.current = true;
    const imageUrl = imageData ? imageData.tools.image.href : '';
    setControlledAltSum((prev) => ({
      ...prev,
      ...DEFAULT_MEDIA_ALT_SUMM,
      image: imageData ? [imageData.tools.image] : [],
      imageUrl,
      imageCredit: imageData?.tools.image.credit ?? ''
    }));
    setPreviewUrl(imageUrl);
  }, []);

  const handleChangeForVideo = useCallback(
    (videoData: VideoDataForSummarian | null) => {
      hasUserChangedMediaRef.current = true;
      const videoUrl = (videoData?.guid && `${publicationSettings?.snippetyVideoDomain}${videoData.guid}`) ?? '';
      setControlledAltSum((prev) => ({
        ...prev,
        ...DEFAULT_MEDIA_ALT_SUMM,
        video: videoData ? [videoData] : [],
        videoUrl,
        videoImage: videoData?.sizes!.full.url ?? ''
      }));
      setPreviewUrl(videoUrl);
    },
    [publicationSettings]
  );

  const handleChangeForChart = useCallback((chartData: SnippetyChartData | null) => {
    hasUserChangedMediaRef.current = true;
    const chartUrl = chartData?.media ?? '';
    const chartId = (chartData?.id && `cdc_${chartData.id}`) ?? '';
    setControlledAltSum((prev) => ({
      ...prev,
      ...DEFAULT_MEDIA_ALT_SUMM,
      chart: chartUrl,
      chartlosId: chartId
    }));
    setPreviewUrl(chartUrl);
  }, []);

  const handleChangeForIframe = useCallback((data: AddIframeData) => {
    hasUserChangedMediaRef.current = true;
    const iframeAltSumm = buildIframeFromAddIframeData(data);
    setControlledAltSum((prev) => ({
      ...prev,
      ...DEFAULT_MEDIA_ALT_SUMM,
      iframe: iframeAltSumm
    }));
    setPreviewUrl(iframeAltSumm.properties.url);
  }, []);

  const handleUpdateBullets = (newBullets: string[]) => {
    setControlledAltSum((prev: ControlledAltSumm) => ({
      ...prev,
      bullets: newBullets
    }));
  };

  const handleIdAdd = useCallback(
    (data: SnippetyResponseWithImage | VideoDataForSummarian | SnippetyChartData | null) => {
      if (inputType === 'image') {
        handleChangeForImage(data as SnippetyResponseWithImage);
      } else if (inputType === 'video') {
        handleChangeForVideo(data as VideoDataForSummarian);
      } else if (inputType === 'chart') {
        handleChangeForChart(data as SnippetyChartData);
      } else if (!data) {
        setControlledAltSum((prev) => ({
          ...prev,
          ...DEFAULT_MEDIA_ALT_SUMM
        }));
        setPreviewUrl('');
      }
    },
    [handleChangeForChart, handleChangeForImage, handleChangeForVideo, inputType]
  );

  const { id, error, onDelete, onIdAdd, onIdChange, shouldAdd, isIdFormOpen, setIsIdFormOpen } = useMediaForm(
    inputType,
    handleIdAdd,
    setControlledAltSum,
    publicationSettings
  );

  const { onImageLoad, clearPreview, onResetPreview, isMediaLoaded, computedAspectRatio, previewType } =
    useMediaPreview({
      setIsIdFormOpen,
      setIsIframeModalOpen,
      setPreviewUrl,
      imagePreviewRef,
      imageUrl: controlledAltSum.imageUrl,
      videoUrl: controlledAltSum.videoUrl,
      chartUrl: controlledAltSum.chart,
      iframe: controlledAltSum.iframe,
      setInputType,
      setControlledAltSum
    });

  const handleDeleteMedia = useCallback(() => {
    hasUserChangedMediaRef.current = true;
    clearPreview();
  }, [clearPreview]);

  useEffect(() => {
    if (flashlineRef.current) {
      flashlineRef.current.style.height = '0';
      flashlineRef.current.style.height = `${flashlineRef.current.scrollHeight}px`;
    }
    if (headlineRef.current) {
      headlineRef.current.style.height = '0';
      headlineRef.current.style.height = `${headlineRef.current.scrollHeight}px`;
    }
    if (bodyRef.current) {
      bodyRef.current.style.height = '0';
      bodyRef.current.style.height = `${bodyRef.current.scrollHeight}px`;
    }
  }, [controlledAltSum.flashline, controlledAltSum.headline, controlledAltSum.body]);

  useEffect(() => {
    // Seed once on open; skip on all subsequent renders to protect user edits.
    if (initializedRef.current) return;
    setControlledAltSum((prev) => ({ ...prev, flashline }));
  }, [flashline]);

  useEffect(() => {
    // Seed once on open; skip on all subsequent renders to protect user edits.
    if (initializedRef.current) return;
    setControlledAltSum((prev) => ({ ...prev, headline }));
  }, [headline]);

  useEffect(() => {
    // Seed once on open; skip on all subsequent renders to protect user edits.
    if (initializedRef.current) return;
    setControlledAltSum((prev) => ({ ...prev, body }));
  }, [body]);

  useEffect(() => {
    // Seed once on open; skip on all subsequent renders to protect user edits.
    if (initializedRef.current) return;
    setControlledAltSum((prev) => ({ ...prev, imageUrl: image }));
  }, [image]);

  useEffect(() => {
    // Seed once on open; skip on all subsequent renders to protect user edits.
    if (initializedRef.current) return;
    setControlledAltSum((prev) => ({ ...prev, iframe }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(iframe)]);

  useEffect(() => {
    // Seed once on open; skip on all subsequent renders to protect user edits.
    if (initializedRef.current) return;
    setControlledAltSum((prev) => ({ ...prev, imageCredit }));
  }, [imageCredit]);

  useEffect(() => {
    // Seed once on open; skip on all subsequent renders to protect user edits.
    if (initializedRef.current) return;
    setControlledAltSum((prev) => ({ ...prev, videoUrl: video }));
  }, [video]);

  useEffect(() => {
    // Seed once on open; skip on all subsequent renders to protect user edits.
    if (initializedRef.current) return;
    setControlledAltSum((prev) => ({ ...prev, videoImage }));
  }, [videoImage]);

  useEffect(() => {
    // Seed once on open; skip on all subsequent renders to protect user edits.
    if (initializedRef.current) return;
    setControlledAltSum((prev) => ({ ...prev, bullets }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(bullets)]);

  useEffect(() => {
    // Seed once on open; skip on all subsequent renders to protect user edits.
    if (initializedRef.current) return;
    setControlledAltSum((prev) => ({ ...prev, subhead }));
  }, [subhead]);

  useEffect(() => {
    // Seed once on open; skip on all subsequent renders to protect user edits.
    if (initializedRef.current) return;
    setControlledAltSum((prev) => ({ ...prev, chartlosId }));
  }, [chartlosId]);

  useEffect(() => {
    // Seed once on open; skip on all subsequent renders to protect user edits.
    if (initializedRef.current) return;
    setControlledAltSum((prev) => ({ ...prev, chart }));
    // All fields seeded — block any further updates for the lifetime of this modal instance.
    initializedRef.current = true;
  }, [chart]);

  const handleToggleModal = () => {
    if (!setIsModalOpenControlled) {
      setIsModalOpen(!isModalOpen);
    } else {
      setIsModalOpenControlled(!isModalOpenControlled);
    }
  };
  const handleCancel = () => handleToggleModal();

  const handleChangeForTextFields =
    (field: keyof typeof controlledAltSum) => (inputEvent: React.ChangeEvent<HTMLTextAreaElement>) => {
      setControlledAltSum((prev) => ({ ...prev, [field]: inputEvent.target.value }));
    };

  const updateAltSumThroughSummarian = async () => {
    /* eslint-disable @typescript-eslint/await-thenable */
    const proxy = await window.summarianShareDbAPI.proxy(content.data.id, altSummVariant);

    await sleep(1000); // Wait one second for proxy to be ready

    const flashlinePersistedValue = await proxy.flashline;
    if (controlledAltSum.flashline !== flashlinePersistedValue) {
      proxy.flashline = encodeHtmlEntities(controlledAltSum.flashline);
      await proxy.flashline;
    }

    const headlinePersistedValue = await proxy.headline;
    if (controlledAltSum.headline !== headlinePersistedValue) {
      proxy.headline = encodeHtmlEntities(controlledAltSum.headline);
      await proxy.headline;
    }

    const bodyPersistedValue = await proxy.body;
    if (controlledAltSum.body !== bodyPersistedValue) {
      proxy.body = encodeHtmlEntities(controlledAltSum.body);
      await proxy.body;
    }

    const imagePersistedValue = await proxy.images;
    if (hasUserChangedMediaRef.current && controlledAltSum.imageUrl !== imagePersistedValue?.[0]?.href) {
      proxy.images = controlledAltSum.image;
      await proxy.images;
    }

    const videoPersistedValue = await proxy.videos;
    const videoId = controlledAltSum.videoUrl.split('?guid=')[1];
    if (hasUserChangedMediaRef.current && videoId !== videoPersistedValue?.[0]?.guid) {
      proxy.videos = controlledAltSum.video;
      await proxy.videos;
    }

    const videoImagePersistedValue = (await proxy.videoImage) as string;
    if (
      hasUserChangedMediaRef.current &&
      controlledAltSum.videoUrl &&
      controlledAltSum.video[0]?.sizes?.full.url !== videoImagePersistedValue
    ) {
      proxy.videoImage = controlledAltSum.videoImage;
      await proxy.videoImage;
    }

    const chartlosIdPersistedValue = await proxy.chartlosId;
    if (controlledAltSum.chartlosId !== chartlosIdPersistedValue) {
      proxy.chartlosId = controlledAltSum.chartlosId;
      await proxy.chartlosId;
    }

    const iframePersistedValue = await proxy.insets;
    if (hasUserChangedMediaRef.current) {
      const nonIframeInsets = (iframePersistedValue ?? []).filter((inset) => inset.inset_type !== 'IFRAME');
      const newInsets: AltSummInset[] = controlledAltSum.iframe.properties.url
        ? [...nonIframeInsets, controlledAltSum.iframe]
        : nonIframeInsets;
      if (!isEqual(newInsets, iframePersistedValue ?? [])) {
        proxy.insets = newInsets;
        await proxy.insets;
      }
    }

    const bulletsPersistedValue = await proxy.bullets;
    if (!arrayEquals(controlledAltSum.bullets, [...(bulletsPersistedValue || [])])) {
      const escapedBullets = controlledAltSum.bullets.map((bullet) => {
        const scriptTagRegex = /<a[^>]*href=["']([^"']*)["']/g;
        const headlineRegex = /<a[^>]*href=["'][^"']*["']>([^<]*)<\/a>/g;
        const bulletLink = scriptTagRegex.exec(bullet);
        const bulletHeadline = headlineRegex.exec(bullet);
        const link = bullet.split('">')[0].slice(9);
        const headline = bullet.split('">')[1].slice(0, -4);
        if (bulletLink && bulletHeadline) {
          return formatBulletLink(encodeHtmlEntities(bulletLink[1]), encodeHtmlEntities(bulletHeadline[1]));
        }
        return formatBulletLink(encodeHtmlEntities(link), encodeHtmlEntities(headline));
      });
      proxy.bullets = escapedBullets;
      await proxy.bullets;
    }

    const subheadPersistedValue = await proxy.subhead;
    if (controlledAltSum.subhead !== subheadPersistedValue) {
      proxy.subhead = controlledAltSum.subhead;
      await proxy.subhead;
    }

    const version = await window.summarianShareDbAPI.currentTemplateVersion(content.data.id, altSummVariant);
    await window.summarianShareDbAPI.publishTemplateAndContent(content.data.id, altSummVariant, version);
    setOverride(content.data.id, altSummVariant, {
      flashline: decodeHtmlEntities(controlledAltSum.flashline),
      headline: decodeHtmlEntities(controlledAltSum.headline),
      body: decodeHtmlEntities(controlledAltSum.body),
      // Clear image when video or chart is present so useMediaPreview shows the right media type.
      // Fall back to CAPI default image when no image is set (e.g. after the user deletes an image override).
      image:
        controlledAltSum.videoUrl || controlledAltSum.chart || controlledAltSum.iframe.properties.url
          ? ''
          : controlledAltSum.imageUrl || getDefaultImage(resolvedContent, altSummVariant),
      imageCredit: controlledAltSum.imageCredit,
      video: controlledAltSum.videoUrl,
      videoUrl: controlledAltSum.videoUrl,
      videoImage: controlledAltSum.videoImage,
      chart: controlledAltSum.chart,
      chartlosId: controlledAltSum.chartlosId,
      bullets: controlledAltSum.bullets.map(decodeHtmlEntities),
      bulletsStructured: controlledAltSum.bullets.map((b) => {
        const match = /<a[^>]*href=["']([^"']*)["'][^>]*>([^<]*)<\/a>/.exec(b);
        return match
          ? { link: decodeHtmlEntities(match[1]), text: decodeHtmlEntities(match[2]) }
          : { text: decodeHtmlEntities(b), link: '' };
      }),
      subhead: controlledAltSum.subhead,
      iframe: controlledAltSum.iframe
    });
    /* eslint-enable @typescript-eslint/await-thenable */
  };

  return (
    <Overlay data-testid="alt-summ-modal-view" onDismiss={handleToggleModal} status="open" renderNode={renderNode}>
      {isIframeModalOpen ? (
        <AddIframeModal
          onCancel={() => setIsIframeModalOpen(false)}
          onAdd={(data) => {
            handleChangeForIframe(data);
            setIsIframeModalOpen(false);
          }}
          publicationKey={publicationSettings?.publicationKey}
          initialData={
            controlledAltSum.iframe.properties.url
              ? {
                  iframeUrl: controlledAltSum.iframe.properties.url,
                  fallbackImageUrl: controlledAltSum.iframe.properties.fallback.url,
                  fallbackLinkUrl: controlledAltSum.iframe.properties.fallback.link_url,
                  fallbackLinkText: controlledAltSum.iframe.properties.fallback.link_text ?? ''
                }
              : undefined
          }
        />
      ) : (
        <Box data-testid="alt-summ-modal-container" className={styles.modalBox}>
          <Box.Title data-testid="alt-summ-modal-title">Edit Alt-Summ</Box.Title>
          <Box.Content data-testid="alt-summ-modal-content" padding={{ all: 'none' }}>
            <Wrapper data-testid="alt-summ-modal-body" padding={{ top: 'md', right: 'mlg', bottom: 'md', left: 'mlg' }}>
              <Group className={styles.groupsWrapper}>
                <Group.Item flex>
                  <Box>
                    <Box.Content padding={{ all: 'md' }} className={styles.dynamicContentBox}>
                      <Typography data-testid="alt-summ-modal-type" variant="header4">
                        {altSummVariant}
                      </Typography>
                      {previewUrl && (
                        <MediaInputPreview
                          computedAspectRatio={computedAspectRatio}
                          onDelete={handleDeleteMedia}
                          onResetPreview={onResetPreview}
                          previewUrl={previewUrl}
                          controlledAltSum={controlledAltSum}
                          onUrlLoaded={onImageLoad}
                          isUrlLoaded={isMediaLoaded}
                          imgRef={imagePreviewRef}
                          previewType={previewType}
                          isVisible={!isIdFormOpen}
                          viewOnly={viewOnly}
                        />
                      )}
                      <MediaInputForm
                        onIdChange={onIdChange}
                        onDelete={onDelete}
                        id={id}
                        error={error}
                        onIdAdd={onIdAdd}
                        isIdFormOpen={!!isIdFormOpen}
                        setIsIdFormOpen={setIsIdFormOpen}
                        shouldAdd={shouldAdd}
                        setVariant={setInputType}
                        inputType={inputType}
                        setControlledAltSum={setControlledAltSum}
                        viewOnly={viewOnly}
                      />
                      <Wrapper
                        data-testid="alt-summ-modal-form"
                        className={styles.inputsWrapper}
                        margin={{ top: 'md' }}
                      >
                        {!disabledAltSummFields.has('flashline') && (
                          <textarea
                            data-testid="alt-summ-modal-flashline-input"
                            className={styles.flashlineTextarea}
                            onChange={handleChangeForTextFields('flashline')}
                            value={decodeHtmlEntities(controlledAltSum.flashline)}
                            ref={flashlineRef}
                            placeholder="Flashline (optional)"
                            disabled={viewOnly}
                          />
                        )}
                        {!disabledAltSummFields.has('headline') && (
                          <>
                            <textarea
                              data-testid="alt-summ-modal-headline-input"
                              className={styles.headlineTextarea}
                              onChange={handleChangeForTextFields('headline')}
                              value={decodeHtmlEntities(controlledAltSum.headline)}
                              ref={headlineRef}
                              placeholder="Headline"
                              disabled={viewOnly}
                            />
                            <span data-testid="alt-summ-modal-headline-counter" className={styles.headlineCharCount}>
                              {controlledAltSum.headline.length}
                            </span>
                          </>
                        )}
                        {!disabledAltSummFields.has('body') && (
                          <textarea
                            data-testid="alt-summ-modal-body-input"
                            className={styles.bodyTextarea}
                            onChange={handleChangeForTextFields('body')}
                            value={decodeHtmlEntities(controlledAltSum.body)}
                            ref={bodyRef}
                            placeholder="Summary"
                            disabled={viewOnly}
                          />
                        )}
                      </Wrapper>
                      {!disabledAltSummFields.has('bullets') && (
                        <BulletInput
                          currentBullets={controlledAltSum.bullets}
                          onExecuteUpdate={handleUpdateBullets}
                          onDisplayNewBulletInput={setDisplayNewBulletInput}
                          displayNewBulletInput={displayNewBulletInput}
                          viewOnly={viewOnly}
                        />
                      )}
                    </Box.Content>
                  </Box>
                </Group.Item>
                <Group.Item data-testid="alt-summ-modal-form" className={styles.groupsItem} flex>
                  <Wrapper>
                    {!disabledAltSummFields.has('image') && (
                      <MediaInputButton
                        variant="image"
                        setVariant={setInputType}
                        isIdFormOpen={!!isIdFormOpen}
                        setIsIdFormOpen={setIsIdFormOpen}
                        disabled={viewOnly || !!previewUrl || inputType === 'video' || inputType === 'chart'}
                      />
                    )}
                    {!disabledAltSummFields.has('video') && (
                      <MediaInputButton
                        variant="video"
                        setVariant={setInputType}
                        isIdFormOpen={!!isIdFormOpen}
                        setIsIdFormOpen={setIsIdFormOpen}
                        disabled={viewOnly || !!previewUrl || inputType === 'image' || inputType === 'chart'}
                      />
                    )}
                    {!disabledAltSummFields.has('chart') && (
                      <MediaInputButton
                        variant="chart"
                        setVariant={setInputType}
                        isIdFormOpen={!!isIdFormOpen}
                        setIsIdFormOpen={setIsIdFormOpen}
                        disabled={viewOnly || !!previewUrl || inputType === 'image' || inputType === 'video'}
                      />
                    )}
                    {!disabledAltSummFields.has('iframe') && (
                      <MediaInputButton
                        variant="iframe"
                        setVariant={setInputType}
                        isIdFormOpen={!!isIdFormOpen}
                        setIsIdFormOpen={(open) => {
                          if (open) {
                            setIsIframeModalOpen(true);
                          } else {
                            setIsIdFormOpen(open);
                          }
                        }}
                        disabled={
                          viewOnly ||
                          !!previewUrl ||
                          inputType === 'image' ||
                          inputType === 'video' ||
                          inputType === 'chart'
                        }
                      />
                    )}
                    {!disabledAltSummFields.has('bullets') && (
                      <Button
                        data-testid="media-bullet-button"
                        onClick={() => setDisplayNewBulletInput(true)}
                        tertiary
                        icon={IconList}
                        disabled={viewOnly}
                      >
                        Add Bullet
                      </Button>
                    )}
                  </Wrapper>
                </Group.Item>
              </Group>
            </Wrapper>
            <Wrapper
              data-testid="alt-summ-modal-footer"
              padding={{ top: 'sm', right: 'mlg', bottom: 'md', left: 'mlg' }}
            >
              <Group data-testid="alt-summ-modal-buttons" gap="sm" className={styles.footerButtonsWrapper} align="end">
                <Button data-testid="alt-summ-modal-cancel-button" secondary onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  data-testid="alt-summ-modal-update-button"
                  primary
                  disabled={isUpdating || isIdFormOpen || viewOnly}
                  onClick={async () => {
                    setIsUpdating(true);
                    await updateAltSumThroughSummarian();

                    if (itpRoot?.type === 'Issue') {
                      const updatedRoot = cloneDeep(itpRoot);

                      if ((itpRoot.attributes.status as ItpIssueStatus) === ItpIssueStatus.Published) {
                        updatedRoot.attributes.status = ItpIssueStatus.Changed;
                      }

                      updateIssue({ root: updatedRoot });
                      setHasAltSummChanged(true);
                    }

                    if (!setIsModalOpenControlled) {
                      setIsModalOpen(!isModalOpen);
                    } else {
                      setIsModalOpenControlled(!isModalOpenControlled);
                    }
                    setIsUpdating(false);
                  }}
                >
                  Apply
                </Button>
              </Group>
            </Wrapper>
          </Box.Content>
        </Box>
      )}
    </Overlay>
  );
};
